/* tslint:disable no-input-rename member-ordering */
// Added ViewEncapsulation
import {
  Component,
  ContentChild,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms'
import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling'
import { ConnectedPosition } from '@angular/cdk/overlay'
import {
  DOWN_ARROW,
  END,
  ENTER,
  ESCAPE,
  hasModifierKey,
  HOME,
  LEFT_ARROW,
  RIGHT_ARROW,
  SPACE,
  TAB,
  UP_ARROW,
} from '@angular/cdk/keycodes'
import { BehaviorSubject, Subject, Subscription } from 'rxjs'
import { delay, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators'
import * as lodash from 'lodash'
import { SelectActionButtonDirective, SelectItemDirective, SelectLabelDirective } from './select.directives'
import { MatSelect } from '@angular/material/select'
import { MatFormFieldControl } from '@angular/material/form-field'
import { MatFormField2Component } from '../../mat-form-field2/mat-form-field2.component'
import { ErrorStateMatcher } from '@angular/material/core'
import { MatOption } from '@angular/material/core'

/**
 * Type representing either a single value or an array of values
 */
export type Selection<T> = T | T[]

/**
 * Interface for grouped items in the select component
 */
export interface GroupedItems<T> {
  groupName: string
  items: T[]
}

// TODO:
// - improve panel max height, as mat-select set a fixed panel max height to SELECT_PANEL_MAX_HEIGHT = 256,
//   that causes mismatch panel postion in some cases as SelectComponent has different max height.
// - improve calculating max width, currently it addes a dummay item of the longest label to top
//   to leverage mat-select auto sizing, however this won't work well with custom item content.

/**
 * Advanced select component with search, grouping, and virtual scrolling capabilities
 * Extends Angular Material's mat-select with additional features
 */
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: SelectComponent }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectComponent<T = any, V = T>
  implements OnInit, OnDestroy, DoCheck, OnChanges, ControlValueAccessor, MatFormFieldControl<Selection<V>>
{
  /* ========== MatFormFieldControl Implementation ========== */

  static nextId = 0

  /** Control type identifier for styling and identification */
  controlType = 'tokeet-select'

  @HostBinding() id = `${this.controlType}-${SelectComponent.nextId++}`
  @HostBinding('attr.aria-describedby') describedBy = ''

  @HostBinding('class') className = 'mat-primary'

  /** Subject that emits when the component state changes */
  stateChanges = new Subject<void>()

  /** Whether the control is in an error state */
  errorState = false

  /** Whether the control is focused */
  focused = false

  /** Whether the control has been touched */
  touched = false

  /** Internal value storage */
  _value: Selection<V> = null

  /** Refined value after validation against available items */
  _refinedValue: Selection<V> = null

  _placeholder: string
  _required = false
  _disabled = false

  /** Map of selected values to their corresponding items */
  valueMap = new Map<V, T>()

  /* ========== MatSelect Pass-through Properties ========== */

  // @Input('aria-label') ariaLabel: string;
  // @Input('aria-labelledby') ariaLabelledby: string;
  @Input() disableOptionCentering: boolean
  @Input() disableRipple: boolean
  @Input() errorStateMatcher: ErrorStateMatcher
  @Input() multiple: boolean
  @Input() panelClass: string | string[] | Set<string> | { [key: string]: any }

  /** Custom comparison function for value equality checks */
  private _compareWith = (o1: any, o2: any) => o1 === o2
  @Input()
  get compareWith() {
    return this._compareWith
  }
  set compareWith(fn: (o1: any, o2: any) => boolean) {
    if (typeof fn !== 'function') {
      console.warn('SelectComponent: compareWith must be a function')
      return
    }
    this._compareWith = fn
  }

  @Input() sortComparator: (a: MatOption, b: MatOption, options: MatOption[]) => number

  /* ========== Select Component Specific Properties ========== */

  /** Height of each item in pixels for virtual scrolling */
  @Input() itemHeight = 40

  /** Number of items visible in the dropdown */
  @Input() visibleItems = 7

  /** Height of group tabs when grouping is enabled */
  groupTabHeight = 30

  /** Internal storage for all items */
  _items: T[] = []

  /** Items organized by groups */
  groupedItems: GroupedItems<T>[] = []

  /** Names of all groups for tab display */
  groupNames: string[] = []

  @Input() set items(value: T[]) {
    this._items = value || []
    this._groupItems() // Re-group items when changed
  }
  get items(): T[] {
    return this._items
  }

  /** Property path or function to extract display label from items */
  @Input() bindLabel: string | string[] | ((item: T) => string)

  /** Property path or function to extract value from items */
  @Input() bindValue: string | string[] | ((item: T) => V)

  /** Function to get tooltip text for each item */
  @Input() bindTooltip: string | ((item: T) => string)

  /** Property path or function to group items */
  _groupBy: string | ((item: T) => any)
  @Input() set groupBy(value: string | ((item: T) => any)) {
    this._groupBy = value
    this._groupItems() // Re-group items when groupBy changes
  }
  get groupBy(): string | ((item: T) => any) {
    return this._groupBy
  }

  /** Whether to show search input */
  @Input() searchable = false

  /** Custom search comparison function */
  @Input() searchCompare: (item: T, search: string) => boolean

  /** Function to determine if an item is selectable */
  @Input() isSelectable: (item: T) => boolean

  /** Position for tooltips */
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' | 'before' | 'after' = 'right'

  /** Debounce time for search input in milliseconds */
  @Input() searchDebounceTime = 100

  /** Placeholder text for search input */
  @Input() searchPlaceholder = $localize`Search...`

  /** Text shown when no items are available */
  @Input() textNoItems = $localize`No data available`

  /** Text shown when search has no matches */
  @Input() textNoMatch = $localize`No match`

  /** Whether to show master checkbox for select all/none */
  @Input() showMasterCheckbox = true

  /** Whether to show close button in the panel */
  @Input() showCloseButton = false

  /** Whether to show a blank/none option */
  @Input() showBlankItem = false

  /** Label for the blank option */
  @Input() blankLabel = 'None'

  /** Value for the blank option */
  @Input() blankValue = null

  /** Emits when the select panel opens or closes */
  @Output() openedChange = new EventEmitter<boolean>()

  /** Emits when search value changes */
  @Output() searchChange = new EventEmitter<string>()

  /** Emits when selection changes (values) */
  @Output() selectionChange = new EventEmitter<Selection<V>>()

  /** Emits when selection changes (items) */
  @Output() selectedChange = new EventEmitter<Selection<T>>()

  /** Reference to the underlying mat-select component */
  _matSelect: MatSelect
  get matSelect(): MatSelect {
    return this._matSelect
  }
  @ViewChild('matSelect', { static: true }) set matSelect(value: MatSelect) {
    this._matSelect = value
    // Configure mat-select positioning. Inside mat-form-field2 the panel attaches to the field box;
    // otherwise to the select trigger, 15px lower to clear the trigger padding (the legacy _offsetY).
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: -40,
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'center',
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom',
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'bottom',
      },
    ]
    const offsetY = this.formField2 ? 0 : 15
    this._matSelect._positions = positions.map((position) => ({ offsetY, ...position }))
    if (this.formField2) this._matSelect._preferredOverlayOrigin = this.formField2.connectionContainer
  }

  /** Reference to the virtual scroll viewport */
  @ViewChild('scroller') scroller: CdkVirtualScrollViewport

  /** Reference to the search input element */
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>

  /** Content directives for custom templates */
  @ContentChild(SelectItemDirective) selectItemDirective: SelectItemDirective<T>
  @ContentChild(SelectLabelDirective) selectLabelDirective: SelectLabelDirective<T, V>
  @ContentChild(SelectActionButtonDirective) selectActionButtonDirective: SelectActionButtonDirective

  /** Observable streams for component state */
  filteredItems$ = new BehaviorSubject<T[]>([])
  focusedItemIndex$ = new BehaviorSubject<number>(-1)
  selectedGroupIndex$ = new BehaviorSubject<number>(0)

  /** Widest item for panel width calculation */
  widestItem$ = new BehaviorSubject<T>(null)

  /** Form control for search input */
  searchControl = new UntypedFormControl()

  /** Whether the select panel is open */
  isOpen = false

  /** Subscription management */
  searchSub: Subscription
  groupSub: Subscription

  /** Subject for component destruction */
  private destroy$ = new Subject<void>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef,
    public focusMonitor: FocusMonitor,
    @Optional() private formField2?: MatFormField2Component
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this
    }
  }

  /**
   * Helper method to find a key in a map using the compareWith function
   * @param value - The value to search for
   * @param map - The map to search in
   * @returns The matching key or undefined
   */
  private findKeyInMap(value: V, map: Map<V, T>): V | undefined {
    return Array.from(map.keys()).find((key) => this.compareWith(key, value))
  }

  /**
   * Helper method to check if a value exists in a map using the compareWith function
   * @param value - The value to check
   * @param map - The map to check in
   * @returns True if the value exists in the map
   */
  private hasValueInMap(value: V, map: Map<V, T>): boolean {
    return this.findKeyInMap(value, map) !== undefined
  }

  /**
   * Helper method to check if an item is selectable
   * @param item - The item to check
   * @returns True if the item is selectable
   */
  isItemSelectable(item: T): boolean {
    if (!this.isSelectable) return true
    return this.isSelectable(item)
  }

  /**
   * Helper method to get the count of selectable items in a list
   * @param items - The list of items to check
   * @returns The count of selectable items
   */
  private getSelectableItemCount(items: T[]): number {
    return items.filter((item) => this.isItemSelectable(item)).length
  }

  /**
   * Helper method to determine if master checkbox should be shown
   * @param filteredItems - Currently visible items
   * @returns True if master checkbox should be shown
   */
  shouldShowMasterCheckbox(filteredItems: T[]): boolean {
    if (!this.multiple || !this.showMasterCheckbox) return false
    return this.getSelectableItemCount(filteredItems) > 0
  }

  /**
   * Helper method to get tooltip text for an item
   * @param item - The item to get tooltip for
   * @returns Tooltip text or empty string if no tooltip should be shown
   */
  getItemTooltip(item: T): string {
    const tooltip = this.getTooltip(item)
    return tooltip && tooltip.trim() ? tooltip.trim() : ''
  }

  ngOnInit() {
    if (this.formField2 && !this._matSelect._preferredOverlayOrigin) {
      this._matSelect._preferredOverlayOrigin = this.formField2.connectionContainer
    }

    // Monitor focus changes on the component
    this.focusMonitor
      .monitor(this.elementRef.nativeElement, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((origin) => this.handleFocus(!!origin))

    this.toggleSearchSubscribe(this.searchable)

    // Subscribe to group changes to reset filter and focus
    this.selectedGroupIndex$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.focusedItemIndex$.next(-1) // Reset focus when group changes
      this.filterItems() // Re-filter items for the new group
    })
  }

  ngOnDestroy() {
    // Clean up all subscriptions and resources
    this.destroy$.next()
    this.destroy$.complete()
    this.stateChanges.complete()
    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement)
    this.toggleSearchSubscribe(false)
  }

  ngDoCheck(): void {
    // Update error state based on form control validity
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched
      this.stateChanges.next()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.searchable) {
      this.toggleSearchSubscribe(changes.searchable.currentValue)
    }

    // Items or groupBy changes trigger regrouping and updates
    if (changes.items || changes.groupBy) {
      // Defer updates to next tick to ensure DOM is ready
      setTimeout(() => {
        this.updateWidestItems() // Calculate widest item for panel sizing
        this.updateRefinedValue() // Validate current value against new items
        this.updateValueMap(this.value) // Update value-to-item mapping
        this.filterItems() // Apply initial filtering

        this.stateChanges.next()
      })
    }
  }

  /* ========== ControlValueAccessor Implementation ========== */

  private _onChange = (value: Selection<V>) => {}
  private _onTouched = () => {
    this.touched = true
  }

  writeValue(value: Selection<V>): void {
    this.value = value
  }

  registerOnChange(fn): void {
    this._onChange = fn
  }

  registerOnTouched(fn): void {
    this._onTouched = fn
  }

  /* ========== Property Getters/Setters ========== */

  @Input()
  get value() {
    return this._refinedValue
  }
  set value(value: Selection<V>) {
    this._value = value
    this.updateValueMap(value)
    this.updateRefinedValue()

    if (this._onChange) this._onChange(value)
    this.stateChanges.next()
  }

  @Input()
  get placeholder() {
    return this._placeholder
  }
  set placeholder(placeholder: string) {
    this._placeholder = placeholder
    this.stateChanges.next()
  }

  @Input()
  get required() {
    return this._required
  }
  set required(required) {
    this._required = coerceBooleanProperty(required)
    this.stateChanges.next()
  }

  @Input()
  get disabled() {
    return this._disabled
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis)
    this.stateChanges.next()
  }

  get empty() {
    return this.isEmpty(this.value)
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ')
  }

  /**
   * Handles focus state changes
   * @param focused - Whether the component is focused
   */
  handleFocus = (focused: boolean) => {
    this.focused = focused

    setTimeout(() => {
      this.stateChanges.next()
    })
  }

  /**
   * Checks if a value is empty
   * @param val - Value to check
   * @returns True if the value is empty
   */
  isEmpty(val): boolean {
    return (
      (lodash.isObject(val) && lodash.isEmpty(val)) ||
      lodash.isUndefined(val) ||
      lodash.isNull(val) ||
      (val as any) === ''
    )
  }

  /**
   * Handles clicks on the form field container
   */
  onContainerClick() {
    if (!this.focused) {
      this.matSelect.open()
    }
  }

  /* ========== Panel and Display Methods ========== */

  /**
   * Calculates the height of the scrollable area based on number of items
   * @param itemsLength - Number of items to display
   * @returns Height in pixels
   */
  getScrollHeight = (itemsLength: number) => {
    itemsLength = itemsLength || 0
    itemsLength = Math.min(itemsLength, this.visibleItems)

    const viewportHeight = document.documentElement.clientHeight

    let height = itemsLength * this.itemHeight

    height = Math.min(height, viewportHeight * 0.7) // max 70% viewport height
    height = Math.max(height, this.itemHeight) // min height for an item showing no match text

    // Add space for tabs if grouping is enabled
    if (this.groupBy && this.groupedItems.length > 1) {
      height = Math.min(height + this.groupTabHeight, viewportHeight * 0.7)
    }

    return height
  }

  /**
   * Builds the panel CSS classes including custom classes
   * @param panelClass - User-provided panel classes
   * @returns Combined panel classes
   */
  getPanelClass = (panelClass: string | string[] | Set<string> | { [key: string]: any }) => {
    let additionalClass = this.controlType + '-panel'

    if (this.multiple && this.showMasterCheckbox !== false) {
      additionalClass += ' ' + this.controlType + '-panel-master-checkbox'
    }
    // Add class when grouped
    if (this.groupBy && this.groupedItems.length > 1) {
      additionalClass += ' ' + this.controlType + '-panel-grouped'
    }

    if (lodash.isString(panelClass)) {
      panelClass = `${additionalClass} ${panelClass}`
    } else if (lodash.isArray(panelClass)) {
      panelClass = [additionalClass, ...panelClass]
    } else if (lodash.isSet(panelClass)) {
      panelClass = new Set(panelClass)
      panelClass.add(additionalClass)
    } else if (lodash.isPlainObject(panelClass)) {
      panelClass = { [additionalClass]: true, ...panelClass }
    } else {
      panelClass = additionalClass
    }

    return panelClass
  }

  /**
   * Handles the opening/closing of the select panel
   * @param opened - Whether the panel is opened
   */
  onOpenedChange(opened: boolean) {
    this.openedChange.emit(opened)

    this.isOpen = opened

    if (opened) {
      let activeIndex = this.focusedItemIndex$.value

      // If not multiple select, try to find the selected item in the current group
      if (activeIndex === -1 && !this.multiple && !this.empty) {
        activeIndex = this.getSelectedItemIndex()
      }

      // If still not found or multiple select, find first selectable item
      if (activeIndex === -1) {
        const currentItems = this.filteredItems$.value
        activeIndex = currentItems.findIndex((item) => this.isItemSelectable(item))
        if (activeIndex === -1) activeIndex = 0 // Fallback to first item if none are selectable
      }

      this.focusedItemIndex$.next(activeIndex)
      this.ensureItemVisible(activeIndex)

      // Reset to the first tab when opening if grouping is enabled
      if (this.groupBy && this.groupedItems.length > 1) {
        this.selectedGroupIndex$.next(0)
      }
    } else {
      this.focusedItemIndex$.next(-1)
    }

    if (opened) {
      if (this.scroller) {
        this.scroller.checkViewportSize()
        setTimeout(() => this.scroller.checkViewportSize(), 500)
      }
      setTimeout(() => {
        if (this.searchable && this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus()
        }
      })
    } else {
      this.clearSearch()
    }
  }

  /**
   * Handles selection changes and emits appropriate events
   * @param value - The new selection value(s)
   */
  onChangeSelection(value: Selection<V>) {
    setTimeout(() => {
      this._onTouched()
      if (this._onChange) this._onChange(value)
      this.stateChanges.next()

      // Update internal value before updating map/refined value
      this._value = value
      this.updateValueMap(this._value)
      this.updateRefinedValue()
      this.selectionChange.emit(value)
      // Filter out potential nulls from map initialization
      this.selectedChange.emit(Array.from(this.valueMap.values()).filter((item) => item !== null))
    })
  }

  /**
   * Finds the index of an item by its value in the filtered items
   * @param value - The value to search for
   * @returns The index of the item or -1 if not found
   */
  getItemIndexByValue(value: V): number {
    const currentItems = this.filteredItems$.value
    return currentItems.findIndex((item) => this.compareWith(this.getValue(item), value))
  }

  /**
   * Gets the index of the selected item in the filtered list
   * @returns The index of the selected item or -1
   */
  getSelectedItemIndex(): number {
    if (this.multiple || this.empty) return -1
    return this.getItemIndexByValue(this.value as V)
  }

  /**
   * Clears the search input
   */
  clearSearch() {
    this.searchControl.setValue('')
  }

  /**
   * Extracts the display label from an item
   * @param item - The item to get the label from
   * @returns The display label
   */
  getLabel = (item: T): string => {
    if (item === this.blankValue) return this.blankLabel
    const bindLabel = this.bindLabel

    if (lodash.isFunction(bindLabel)) return bindLabel(item)
    if (lodash.isString(bindLabel) || lodash.isArray(bindLabel)) return lodash.get(item, bindLabel)

    return lodash.toString(item)
  }

  /**
   * Extracts the value from an item
   * @param item - The item to get the value from
   * @returns The item's value
   */
  getValue = (item: T): V => {
    if (item === this.blankValue) return this.blankValue
    const bindValue = this.bindValue

    if (lodash.isFunction(bindValue)) return bindValue(item)
    if (lodash.isString(bindValue) || lodash.isArray(bindValue)) return lodash.get(item, bindValue)

    return item as any
  }

  /**
   * Extracts the tooltip from an item
   * @param item - The item to get the tooltip from
   * @returns The tooltip string
   */
  getTooltip = (item: T): string => {
    const bindTooltip = this.bindTooltip

    if (lodash.isFunction(bindTooltip)) return bindTooltip(item)
    if (lodash.isString(bindTooltip)) return lodash.get(item, bindTooltip)

    return ''
  }

  /**
   * Creates context object for the trigger template
   * @param valueMap - The current value map
   * @returns Context object with items, values, and removeItem function
   */
  getTriggerContext = (valueMap: Map<V, T>) => {
    // Filter out potential nulls and empty values
    const items = Array.from(valueMap.values()).filter((item) => item !== null)
    const values = Array.from(valueMap.keys()).filter((v) => !this.isEmpty(v) || v === this.blankValue)
    return { items, values, removeItem: (item: T) => this.onItemClick(item) }
  }

  /**
   * Creates the display value for the trigger
   * @param items - Selected items
   * @returns Comma-separated labels
   */
  getTriggerValue = (items: T[]) => {
    const labels = items.map((item) => this.getLabel(item))
    return lodash.compact(labels).join(', ')
  }

  /**
   * Determines if an item is checked/selected
   * @param item - The item to check
   * @param valueMap - The current value map
   * @returns 'checked' if selected, 'unchecked' if not selected, or 'disabled' if not selectable
   */
  getItemChecked = (item: T, valueMap: Map<V, T>) => {
    // Return 'disabled' for non-selectable items
    if (!this.isItemSelectable(item)) {
      return 'disabled'
    }

    // Use compareWith for checking if item is selected
    return Array.from(valueMap.keys()).some((value) => this.compareWith(this.getValue(item), value))
      ? 'checked'
      : 'unchecked'
  }

  /**
   * Determines the state of the master checkbox based on filtered items
   * @param filteredItems - Currently visible items
   * @param valueMap - The current value map
   * @returns 'checked', 'unchecked', or 'indeterminate'
   */
  getMasterChecked = (filteredItems: T[], valueMap: Map<V, T>) => {
    if (!filteredItems || !filteredItems.length || !valueMap) return 'unchecked'

    // Filter to only selectable items
    const selectableItems = filteredItems.filter((item) => this.isItemSelectable(item))
    if (!selectableItems.length) return 'unchecked'

    const currentItemsValues = selectableItems.map((item) => this.getValue(item)).filter((v) => v !== this.blankValue)

    if (!currentItemsValues.length) return 'unchecked'

    let checkedCount = 0
    currentItemsValues.forEach((val) => {
      if (this.hasValueInMap(val, valueMap)) {
        checkedCount++
      }
    })

    if (checkedCount === 0) return 'unchecked'
    if (checkedCount === currentItemsValues.length) return 'checked'

    return 'indeterminate'
  }

  /**
   * Toggles the master checkbox to select/deselect all visible items
   */
  toggleMasterCheckbox = () => {
    const currentItems = this.filteredItems$.value.filter((item) => item !== this.blankValue)
    const state = this.getMasterChecked(currentItems, this.valueMap)

    this.valueMap = new Map(this.valueMap) // Create a new map instance for immutability

    if (state !== 'checked') {
      // Select all selectable items in the current filtered view
      currentItems.forEach((item) => {
        const itemValue = this.getValue(item)
        if (this.isItemSelectable(item) && !this.hasValueInMap(itemValue, this.valueMap)) {
          this.valueMap.set(itemValue, item)
        }
      })
    } else {
      // Deselect all items in the current filtered view
      currentItems.forEach((item) => {
        const itemValue = this.getValue(item)
        const matchingKey = this.findKeyInMap(itemValue, this.valueMap)
        if (matchingKey !== undefined) {
          this.valueMap.delete(matchingKey)
        }
      })
    }

    const values = Array.from(this.valueMap.keys())
    this.onChangeSelection(this.multiple ? values : values[0] ?? null)
  }

  /**
   * Handles item click to toggle selection
   * @param item - The clicked item
   */
  onItemClick(item: T) {
    // Don't allow selection of non-selectable items
    if (!this.isItemSelectable(item)) {
      return
    }

    const itemValue = this.getValue(item)

    if (this.multiple) {
      // Use compareWith to check if value exists
      const matchingKey = this.findKeyInMap(itemValue, this.valueMap)
      const selected = matchingKey !== undefined
      this.valueMap = new Map(this.valueMap) // Create new map instance for immutability

      if (selected) {
        this.valueMap.delete(matchingKey)
      } else {
        this.valueMap.set(itemValue, item)
      }

      const values = Array.from(this.valueMap.keys())
      this.onChangeSelection(values)

      // Update focused index after click
      this.focusedItemIndex$.next(this.getItemIndexByValue(itemValue))
    } else {
      this.valueMap = new Map()
      this.valueMap.set(itemValue, item)

      this.onChangeSelection(itemValue)
      this.close()
    }
  }

  /**
   * Opens the select panel
   */
  open() {
    if (this.matSelect) this.matSelect.open()
  }

  /**
   * Closes the select panel
   */
  close() {
    if (!this.matSelect) return
    this.matSelect.close()
  }

  /**
   * Toggles the select panel open/closed
   */
  toggle() {
    if (this.matSelect) this.matSelect.toggle()
  }

  /**
   * Gets whether the panel is currently open
   */
  get panelOpen() {
    return this.matSelect ? this.matSelect.panelOpen : false
  }

  /**
   * Groups items based on the groupBy property or function
   * @private
   */
  private _groupItems() {
    if (!this.groupBy) {
      this.groupedItems = [{ groupName: 'all', items: [...this._items] }]
      this.groupNames = []
      this.selectedGroupIndex$.next(0)
      return
    }

    const getGroupName = (item: T): string => {
      if (lodash.isFunction(this.groupBy)) {
        return lodash.toString(this.groupBy(item))
      }
      if (lodash.isString(this.groupBy)) {
        return lodash.toString(lodash.get(item, this.groupBy))
      }
      return 'Ungrouped'
    }

    const groups = lodash.groupBy(this._items, getGroupName)
    this.groupedItems = Object.entries(groups).map(([groupName, items]) => ({ groupName, items }))
    this.groupNames = this.groupedItems.map((g) => g.groupName)

    // Reset selected group if it's out of bounds
    if (this.selectedGroupIndex$.value >= this.groupedItems.length) {
      this.selectedGroupIndex$.next(0)
    } else {
      // Trigger update if index is the same but groups changed
      this.selectedGroupIndex$.next(this.selectedGroupIndex$.value)
    }
  }

  /**
   * Handles tab selection change for grouped items
   * @param index - The selected tab index
   */
  selectGroup(index: number) {
    if (index !== this.selectedGroupIndex$.value) {
      this.selectedGroupIndex$.next(index)
      // Focus search input when changing tabs
      setTimeout(() => this.searchInput?.nativeElement?.focus())
    }
  }

  /**
   * Finds and stores the item with the longest label for panel width calculation
   * @private
   */
  private updateWidestItems() {
    let widestItem: T = null
    let maxLabelLength = -1

    // Check across all items
    for (let i = 0; i < this._items?.length; i++) {
      const item = this._items[i]
      const label = this.getLabel(item)

      if (label && label?.length > maxLabelLength) {
        widestItem = item
        maxLabelLength = label?.length
      }
    }

    this.widestItem$.next(widestItem)
  }

  /**
   * Updates the value map with current selection
   * @param val - The current value(s)
   * @private
   */
  private updateValueMap(val) {
    let values: V[] = []

    if (this.multiple) {
      if (lodash.isArray(val)) values = val
    } else if (!this.isEmpty(val)) {
      values = [val as V]
    }

    this.valueMap = new Map()

    if (!values.length) return this.valueMap

    // Initialize map entries with null
    values.forEach((value) => {
      this.valueMap.set(value, null)
    })

    // Populate map with actual selectable items
    this._items.forEach((item) => {
      if (!this.isItemSelectable(item)) return // Skip non-selectable items

      const itemValue = this.getValue(item)
      const matchingValue = Array.from(this.valueMap.keys()).find((value) => this.compareWith(itemValue, value))
      if (matchingValue !== undefined) {
        this.valueMap.set(matchingValue, item)
      }
    })
  }

  /**
   * Sets up or tears down search value change subscription
   * @param enable - Whether to enable search subscription
   * @private
   */
  private toggleSearchSubscribe(enable = true) {
    if (this.searchSub) this.searchSub.unsubscribe()

    if (!enable) return

    this.searchSub = this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        delay(this.searchDebounceTime),
        tap((search: string) => {
          this.searchChange.emit(lodash.trim(search).toLowerCase())
          this.filterItems()
          // Find first selectable item to focus on
          const currentItems = this.filteredItems$.value
          const firstSelectableIndex = currentItems.findIndex((item) => this.isItemSelectable(item))
          this.focusedItemIndex$.next(firstSelectableIndex >= 0 ? firstSelectableIndex : 0)
          this.ensureItemVisible(firstSelectableIndex >= 0 ? firstSelectableIndex : 0)
        })
      )
      .subscribe()
  }

  /**
   * Default search comparison function
   * @param item - Item to compare
   * @param search - Search string
   * @returns True if item matches search
   * @private
   */
  private defaultSearchCompare = (item: T, search: string) => {
    const label = this.getLabel(item)
    return label && lodash.toString(label).toLowerCase().includes(search)
  }

  /**
   * Filters items based on search and grouping
   * @private
   */
  private filterItems = () => {
    let itemsToFilter: T[] = []

    // Get items from selected group or all items
    if (this.groupBy && this.groupedItems.length > 0) {
      const groupIndex = this.selectedGroupIndex$.value
      if (groupIndex >= 0 && groupIndex < this.groupedItems.length) {
        itemsToFilter = this.groupedItems[groupIndex].items
      }
    } else {
      itemsToFilter = this._items
    }

    itemsToFilter = itemsToFilter || []

    const search = lodash.trim(this.searchControl.value).toLowerCase()
    const searchCompare = this.searchCompare || this.defaultSearchCompare

    let filtered: T[] = []
    if (search) {
      filtered = itemsToFilter.filter((item) => {
        // Don't filter the blank item
        if (this.showBlankItem && item === this.blankValue) return true
        return searchCompare(item, search)
      })
    } else {
      filtered = [...itemsToFilter]
    }

    // Add blank item if configured and not already present
    if (this.showBlankItem && !filtered.some((item) => item === this.blankValue)) {
      if (itemsToFilter.length > 0 || !search) {
        filtered.unshift(this.blankValue)
      }
    }

    this.filteredItems$.next(filtered)

    // Update viewport after filtering
    setTimeout(() => this.scroller?.checkViewportSize())
  }

  /**
   * Handles keyboard events on the select element
   * @param event - The keyboard event
   */
  handleSelectKeydown(event: KeyboardEvent) {
    // Prevent mat-select default handling which closes on arrows when not multiple
    if (!this.multiple && !this.isOpen) {
      const { keyCode, isArrowKey } = this.getKeyboardEventInfo(event)
      if (isArrowKey && keyCode !== LEFT_ARROW && keyCode !== RIGHT_ARROW) {
        event.preventDefault()
      }
    }

    if (this.isOpen) {
      this.handleOpenedKeydown(event)
    } else {
      this.handleClosedKeydown(event)
    }
  }

  /**
   * Handles keyboard events on the search input
   * @param event - The keyboard event
   */
  handleInputKeydown(event: KeyboardEvent) {
    const { keyCode } = this.getKeyboardEventInfo(event)
    // Let navigation keys pass through to handleOpenedKeydown
    if ([UP_ARROW, DOWN_ARROW, ENTER, ESCAPE, HOME, END].includes(keyCode)) {
      this.handleOpenedKeydown(event)
    } else if (keyCode === SPACE) {
      event.stopPropagation()
    }
  }

  /**
   * Handles keyboard events from the focus trap element
   * @param event - The keyboard event
   */
  handleItemFocusTrapKeydown(event: KeyboardEvent) {
    event.stopPropagation()
    this.handleOpenedKeydown(event)
  }

  /**
   * Handles keyboard events when the select is closed
   * @param event - The keyboard event
   * @private
   */
  handleClosedKeydown(event: KeyboardEvent) {
    const { keyCode, isArrowKey, isOpenKey } = this.getKeyboardEventInfo(event)

    // Open on Enter, Space, Down Arrow (if not multiple), Alt+Up/Down
    if (
      (isOpenKey && !hasModifierKey(event)) ||
      (keyCode === DOWN_ARROW && !this.multiple) ||
      (event.altKey && (keyCode === UP_ARROW || keyCode === DOWN_ARROW))
    ) {
      event.preventDefault()
      this.open()
    } else if (!this.multiple) {
      // Handle navigation for single select when closed
      if (keyCode === HOME) {
        event.preventDefault()
        this.setItemSelected('first', this._items)
      } else if (keyCode === END) {
        event.preventDefault()
        this.setItemSelected('last', this._items)
      } else if (keyCode === UP_ARROW) {
        event.preventDefault()
        this.setItemSelected('prev', this._items)
      }
    }
  }

  /**
   * Handles keyboard events when the select is open
   * @param event - The keyboard event
   * @private
   */
  handleOpenedKeydown(event: KeyboardEvent) {
    const { keyCode, isSelectKey } = this.getKeyboardEventInfo(event)
    const currentFilteredItems = this.filteredItems$.value

    // Prevent default browser scrolling on navigation keys
    if ([UP_ARROW, DOWN_ARROW, HOME, END].includes(keyCode)) {
      event.preventDefault()
    }

    if (keyCode === HOME) {
      this.setItemFocused('first', currentFilteredItems)
    } else if (keyCode === END) {
      this.setItemFocused('last', currentFilteredItems)
    } else if (keyCode === UP_ARROW) {
      this.setItemFocused('prev', currentFilteredItems)
    } else if (keyCode === DOWN_ARROW) {
      this.setItemFocused('next', currentFilteredItems)
    } else if (keyCode === TAB) {
      // Allow TAB to naturally move focus
      this.close()
    } else if (keyCode === ESCAPE) {
      event.stopPropagation() // Prevent closing parent dialogs
      this.close()
    } else if (isSelectKey && !hasModifierKey(event)) {
      event.preventDefault() // Prevent space from scrolling page
      const focusedIndex = this.focusedItemIndex$.value
      if (focusedIndex >= 0 && focusedIndex < currentFilteredItems.length) {
        const item = currentFilteredItems[focusedIndex]
        // Only select if the item is selectable
        if (this.isItemSelectable(item)) {
          this.onItemClick(item)
        }
      }
    }
  }

  /**
   * Selects an item directly (for single-select keyboard navigation when closed)
   * @param type - Navigation type
   * @param items - Items to navigate through
   * @private
   */
  private setItemSelected(type: 'first' | 'last' | 'prev' | 'next', items: T[]) {
    if (!items || !items.length) return

    const currentIndex = items.findIndex((item) => this.getValue(item) === this.value)
    const newIndex = this.getNextItemIndex(type, currentIndex, items)

    if (newIndex >= 0 && newIndex < items.length) {
      const item = items[newIndex]
      // Only select if the item is selectable
      if (this.isItemSelectable(item)) {
        this.onItemClick(item)
      }
    }
  }

  /**
   * Sets the focused item index within the filtered list
   * @param type - Navigation type
   * @param currentFilteredItems - Currently visible items
   * @private
   */
  private setItemFocused(type: 'first' | 'last' | 'next' | 'prev', currentFilteredItems: T[]) {
    if (!currentFilteredItems || !currentFilteredItems.length) return

    const newIndex = this.getNextItemIndex(type, this.focusedItemIndex$.value, currentFilteredItems)

    // Only set focus if the item is selectable
    if (
      newIndex >= 0 &&
      newIndex < currentFilteredItems.length &&
      this.isItemSelectable(currentFilteredItems[newIndex])
    ) {
      this.focusedItemIndex$.next(newIndex)
      this.ensureItemVisible(newIndex)
    }
  }

  /**
   * Ensures the item at the given index is visible in the viewport
   * @param index - Index of the item to make visible
   * @private
   */
  private ensureItemVisible(index: number) {
    if (!this.scroller || !this.scroller.elementRef || index < 0) return

    // Delay to allow virtual scroll to update
    setTimeout(() => {
      if (!this.scroller || !this.scroller.elementRef) return

      const itemHeight = this.itemHeight
      const scrollElement = this.scroller.elementRef.nativeElement
      const clientHeight = scrollElement.clientHeight

      // Calculate scroll position to bring item into view
      const itemTop = index * itemHeight
      const itemBottom = itemTop + itemHeight
      const currentScrollTop = scrollElement.scrollTop
      const currentScrollBottom = currentScrollTop + clientHeight

      let targetScrollTop = currentScrollTop

      if (itemTop < currentScrollTop) {
        // Item is above the viewport
        targetScrollTop = itemTop
      } else if (itemBottom > currentScrollBottom) {
        // Item is below the viewport
        targetScrollTop = itemBottom - clientHeight
      } else {
        // Item is already visible
        return
      }

      // Ensure scroll stays within bounds
      targetScrollTop = Math.max(0, targetScrollTop)

      this.scroller.scrollToOffset(targetScrollTop, 'smooth')
    }, 50)
  }

  /**
   * Calculates the next index based on navigation type
   * @param type - Navigation type
   * @param activeIndex - Current active index
   * @param items - Items to navigate through
   * @returns The new index
   * @private
   */
  private getNextItemIndex(type: 'first' | 'last' | 'next' | 'prev', activeIndex: number, items: T[]): number {
    const itemCount = items ? items.length : 0
    if (itemCount === 0) return -1

    // Filter to only selectable items for navigation
    const selectableItems = items.filter((item) => this.isItemSelectable(item))
    if (selectableItems.length === 0) return -1

    let index: number

    switch (type) {
      case 'first':
        // Find first selectable item
        index = items.findIndex((item) => this.isItemSelectable(item))
        break
      case 'last':
        // Find last selectable item
        for (let i = items.length - 1; i >= 0; i--) {
          if (this.isItemSelectable(items[i])) {
            index = i
            break
          }
        }
        break
      case 'next':
        // Find next selectable item
        index = activeIndex < 0 ? 0 : activeIndex + 1
        while (index < items.length && !this.isItemSelectable(items[index])) {
          index++
        }
        break
      case 'prev':
        // Find previous selectable item
        index = activeIndex < 0 ? items.length - 1 : activeIndex - 1
        while (index >= 0 && !this.isItemSelectable(items[index])) {
          index--
        }
        break
      default:
        index = activeIndex
    }

    // Clamp index within bounds
    index = Math.min(Math.max(index, 0), itemCount - 1)

    return index
  }

  /**
   * Updates the refined value based on available items
   * @private
   */
  private updateRefinedValue() {
    // Refine value based on all available items
    this._refinedValue = this.getRefinedValue(this._value, this._items)
  }

  /**
   * Validates and refines the value against available items
   * @param value - Value to refine
   * @param allItems - All available items
   * @returns Refined value
   * @private
   */
  private getRefinedValue(value: Selection<V>, allItems: T[] = []) {
    // Filter to only selectable items
    const selectableItems = allItems.filter((item) => this.isItemSelectable(item))

    // For single select
    if (!lodash.isArray(value)) {
      // If value is null/undefined, return blank or null based on showBlankItem
      if (this.isEmpty(value)) {
        return this.showBlankItem ? this.blankValue : null
      }

      // Check if value exists in selectable items using compareWith
      const exists = selectableItems.some((item) => this.compareWith(this.getValue(item), value))
      return exists ? value : this.showBlankItem ? this.blankValue : null
    }
    // For multiple select
    else {
      // Filter values that exist in selectable items using compareWith
      return value.filter((v) => {
        if (this.isEmpty(v)) return false
        return selectableItems.some((item) => this.compareWith(this.getValue(item), v))
      })
    }
  }

  /**
   * Extracts keyboard event information
   * @param event - The keyboard event
   * @returns Object with event details
   * @private
   */
  private getKeyboardEventInfo(event: KeyboardEvent) {
    const keyCode = event.keyCode
    const isArrowKey =
      keyCode === DOWN_ARROW || keyCode === UP_ARROW || keyCode === LEFT_ARROW || keyCode === RIGHT_ARROW
    const isEnterOrSpace = keyCode === ENTER || keyCode === SPACE

    return {
      keyCode,
      isArrowKey,
      isOpenKey: isEnterOrSpace || keyCode === DOWN_ARROW || keyCode === UP_ARROW,
      isSelectKey: isEnterOrSpace,
    }
  }

  /**
   * TrackBy function for virtual scroll performance
   * @param index - Item index
   * @param item - The item
   * @returns Unique identifier for the item
   */
  trackByValue = (index: number, item: T): V | number => {
    return this.getValue(item) ?? index
  }
}
